import {Link, Text, View} from 'native-base';
import React, {useCallback, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {ROUTES} from 'routes';

import {passwordReset} from 'api/profile';
import ReactInputVerificationCode from 'components/ReactInputVerificationCode';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

import {theme} from '../../../theme';
import {PRSubtitle, PRTitle} from '../PRLabels';
import PRPageTemplate from '../PRPageTemplate';
import {PRWarningBannerProps} from '../PRWarningBanner';

const PRCodeEntry = () => {
  const navigate = useNavigateWithParams();
  const location = useLocation();

  const [codeComplete, setCodeComplete] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [code, setCode] = useState('');
  const [authToken, setAuthToken] = useState(
    location?.state?.passwordResetToken,
  );
  const [correlationId, setCorrelationId] = useState(
    location?.state?.correlationId,
  );
  const [APICallFailure, setAPICallFailure] = useState(false);
  const [warning, setWarning] = useState<PRWarningBannerProps | undefined>(
    undefined,
  );

  const {trackAdobeEvent} = useTrackEvent();

  const userInformation = location?.state?.userInformation;
  const reason = location?.state?.reason;

  const submitCode = useCallback(() => {
    navigate(ROUTES.passwordResetNewPassword, {
      state: {
        passwordResetCode: code,
        passwordResetToken: authToken,
        userInformation: userInformation,
        correlationId: correlationId,
      },
    });
  }, [navigate, code, authToken, userInformation, correlationId]);

  const resendCode = async () => {
    setAPICallFailure(false);
    setLoading(true);
    const response = await passwordReset({
      username: userInformation,
    });

    if (response?.data?.header?.resultCode !== 'WCS0000') {
      setLoading(false);
      setAPICallFailure(true);
    } else {
      setLoading(false);
      setAuthToken(response?.data?.body?.authToken);
      setCorrelationId(response?.data?.body?.correlationId);
    }
  };

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      page: {
        name: 'password reset start otp code entry',
      },
    });
  }, []);

  useEffect(() => {
    if (reason) {
      setWarning({
        label: 'Incorrect code. Please try again',
        testID: `${testIDRoot}-incorrect-code`,
      });
    } else if (APICallFailure) {
      setWarning({
        label:
          'A system error occurred, but we’re working on it. Please try again.',
        testID: `${testIDRoot}-system-error`,
      });
    } else {
      setWarning(undefined);
    }
  }, [reason, APICallFailure]);

  const testIDRoot = 'code-entry';
  const footerButtonDisabled =
    code.length < 6 ? true : !codeComplete ? true : false;

  return (
    <PRPageTemplate
      footerCTALabel="Submit"
      footerOnPress={submitCode}
      footerButtonDisabled={footerButtonDisabled || loading}
      testID={testIDRoot}
      loading={loading}
      warning={warning}
    >
      <View>
        <PRTitle testID="code-entry">Enter Verification Code</PRTitle>
        <PRSubtitle>
          We have sent a one time passcode to your email or mobile phone, if an
          associated account is found with that information. Standard message
          and data rates may apply.
        </PRSubtitle>
        <View mt={4} />
        <PRSubtitle>
          Please note, this code is only valid for 5 minutes.
        </PRSubtitle>

        <View
          mt={4}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          display="flex"
        >
          <ReactInputVerificationCode
            isInvalid={false}
            onCompleted={(value) => {
              setCode(value);
              setCodeComplete(true);
            }}
            isOverMaxCodeAttempts={false}
            length={6}
            placeholder=""
            onChange={(value: string) => {
              setCode(value);
            }}
            ariaLabelPrefix="password reset"
          />
        </View>
        <Text fontSize="14px" lineHeight="20px" fontFamily="heading" mt={4}>
          Did not receive a code?{' '}
          <Link
            isUnderlined={false}
            color={theme.colors.caesarRewards}
            fontSize="14px"
            testID={`${testIDRoot}-resend-link`}
            onPress={resendCode}
            _web={{
              style: {cursor: 'pointer'} as any,
            }}
            accessible={true}
            accessibilityLabel="Resend verification code"
            accessibilityRole="link"
            accessibilityHint="Resends verification code"
          >
            Resend
          </Link>{' '}
          or try another{' '}
          <Link
            isUnderlined={false}
            color={theme.colors.caesarRewards}
            fontSize="14px"
            testID={`${testIDRoot}-contact-method-link`}
            onPress={() => navigate(ROUTES.passwordResetInformation)}
            _web={{
              style: {cursor: 'pointer'} as any,
            }}
            accessible={true}
            accessibilityLabel="Try different contact method"
            accessibilityRole="link"
            accessibilityHint="Opens different contact method window"
          >
            contact method
          </Link>
        </Text>
      </View>
    </PRPageTemplate>
  );
};

export default PRCodeEntry;
